// src/pages/AnalyticsPage.jsx - COMPLETE ENHANCED VERSION
// Replace your entire AnalyticsPage.jsx file with this code

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
  LineChart, Line, Legend, ResponsiveContainer, Area, AreaChart
} from "recharts";
import { parseISO, format } from "date-fns";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import Creator from "../components/Creator";
import { BACKEND_URL } from "../utils/apiConfig";
import { useToast } from "../context/ToastContext";
import AnalyticsDetailModal from "../components/AnalyticsDetailModal";
import {
  getAnalyticsRangeWindow,
  validateCustomRange,
  getAnalyticsBookingsForRange,
  buildAnalyticsMetrics,
  buildTrend,
  getPaymentStatusCounts,
  getBookingStatusCounts,
  getReceivedAmount,
} from "../utils/guestRoomAnalytics";

const COLORS = {
  primary: "#DC2626",
  secondary: "#60A5FA", 
  success: "#10B981",
  warning: "#F59E0B",
  info: "#6366F1",
  danger: "#EF4444"
};

// Lucide Icons (inline SVG to avoid import issues)
const TrendingUpIcon = () => (
  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
);

const UsersIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const IndianRupeeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 3h12"></path>
    <path d="M6 8h12"></path>
    <path d="M6 13l8.5 8"></path>
    <path d="M6 13h3a5 5 0 0 0 5-5V8"></path>
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const ClockIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const DownloadIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

// Small hover/focus tooltip. Absolutely positioned, so it never changes layout.
const InfoTip = ({ text, className = "", children }) => (
  <span className={`relative group inline-block cursor-help outline-none ${className}`} tabIndex={0}>
    {children}
    <span
      role="tooltip"
      className="pointer-events-none invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus:visible group-focus:opacity-100 transition-opacity absolute left-0 top-full mt-1 z-20 w-56 rounded-md bg-gray-900 px-2 py-1.5 text-[11px] font-normal leading-snug text-white shadow-lg"
    >
      {text}
    </span>
  </span>
);

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, onClick, tooltip }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    aria-label={onClick ? `${title}: view details` : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    } : undefined}
    className={`bg-white rounded-lg md:rounded-2xl p-4 md:p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow ${
      onClick ? "cursor-pointer hover:border-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500" : ""
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <p className="text-xs md:text-sm text-gray-600 font-medium mb-1">
          {tooltip ? <InfoTip text={tooltip}>{title}</InfoTip> : title}
        </p>
        <h3 className="text-lg md:text-3xl font-bold text-gray-900 mb-2">{value}</h3>
        {subtitle && (
          <p className="text-xs text-gray-500">{subtitle}</p>
        )}
        {trend && (
          <div className={`text-xs mt-2 flex items-center gap-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUpIcon />
            <span>{Math.abs(trend)}% vs last period</span>
          </div>
        )}
      </div>
      <div className="p-2 md:p-3 rounded-lg md:rounded-xl flex-shrink-0" style={{ backgroundColor: `${COLORS[color]}15` }}>
        <Icon className="w-5 h-5 md:w-6 md:h-6" style={{ color: COLORS[color] || color }} />
      </div>
    </div>
  </motion.div>
);

export default function AnalyticsPage({ setActiveTab }) {
  const { showToast } = useToast();
  const [range, setRange] = useState("Monthly");
  const [previousPreset, setPreviousPreset] = useState("Monthly");
  const [customRange, setCustomRange] = useState(null); // applied { from, to } (yyyy-MM-dd)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMetric, setActiveMetric] = useState(null);
  const closeMetric = useCallback(() => setActiveMetric(null), []);
  const { currentUser } = useAuth();
  const role = currentUser?.role || "caretaker";

  // Fetch ALL bookings including historical data
  // `silent` background refreshes keep the page as-is and never alert.
  const fetchAllBookings = useCallback(async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        const API = BACKEND_URL;

        const response = await fetch(`${API}/api/bookings/all-for-download`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json"
          },
        });

        // ✅ Handle 401 Unauthorized
        if (silent && !response.ok) return;
        if (response.status === 401) {
          console.error("❌ Unauthorized - Session may be expired");
          alert("Session expired. Please login again.");
          window.location.href = "/login";
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch bookings: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // ✅ Validate response structure
        if (!data || !data.hostels) {
          console.warn("⚠️ No hostel data returned");
          setAllBookings([]);
          return;
        }

        setLoading(false);
        
        // Extract all bookings from hostel structure
        const bookings = [];
        data.hostels.forEach(hostel => {
          if (hostel.rooms && Array.isArray(hostel.rooms)) {
            hostel.rooms.forEach(room => {
              if (room.bookings && Array.isArray(room.bookings)) {
                room.bookings.forEach(booking => {
                  try {
                    bookings.push({
                      ...booking,
                      hostel: hostel.name,
                      roomNo: room.roomNo,
                      from: parseISO(booking.from),
                      to: parseISO(booking.to),
                    });
                  } catch (dateError) {
                    console.error("❌ Error parsing booking dates:", dateError);
                  }
                });
              }
            });
          }
        });

        console.log("📊 Total bookings fetched:", bookings.length);
        setAllBookings(bookings);
        
      } catch (err) {
        console.error("❌ Failed to fetch analytics data:", err);

        if (silent) return; // keep the last good data on a background refresh failure
        setLoading(false);

        // ✅ Show user-friendly error
        if (err.message.includes("Failed to fetch")) {
          alert("Network error. Please check your connection and try again.");
        } else {
          alert(`Error loading analytics: ${err.message}`);
        }
        
        setAllBookings([]); // Set empty array to prevent crashes
      }
  }, []);

  useEffect(() => {
    fetchAllBookings();
  }, [fetchAllBookings]);

  // Keep Active Guests live: re-fetch on the existing booking socket events
  // (manual/auto checkout, extension, cancel, transfer) and when the tab regains focus.
  useEffect(() => {
    const refresh = () => fetchAllBookings(true);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const events = ["bookingDataUpdated", "guestCheckedOut", "bookingExtended", "bookingCancelled", "bookingTransferred"];
    events.forEach((name) => window.addEventListener(name, refresh));
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      events.forEach((name) => window.removeEventListener(name, refresh));
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchAllBookings]);

  // Lightweight clock so Active Guests drops at checkout time without a reload.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNowTick(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  // Role-based redirect
  useEffect(() => {
    if (role === "caretaker" || role === "warden") {
      setActiveTab("Home");
    }
  }, [role, setActiveTab]);

  const rangeWindow = useMemo(() => getAnalyticsRangeWindow(range, new Date(), customRange), [range, customRange]);

  const filteredBookings = useMemo(
    () => getAnalyticsBookingsForRange(allBookings, range, new Date(), customRange),
    [allBookings, range, customRange]
  );

  const selectPreset = (preset) => {
    setRange(preset);
    setPreviousPreset(preset);
    setCustomRange(null);
    setPickerOpen(false);
  };

  const applyCustomRange = () => {
    const check = validateCustomRange({ from: draftFrom, to: draftTo });
    if (!check.valid) {
      showToast(check.message, "error");
      return;
    }
    setCustomRange({ from: draftFrom, to: draftTo });
    setRange("Custom");
  };

  const clearCustomRange = () => {
    setDraftFrom("");
    setDraftTo("");
    setCustomRange(null);
    setPickerOpen(false);
    setRange(previousPreset);
  };

  // Cards and popups share the SAME rows, derived from current booking state.
  const metrics = useMemo(
    () => buildAnalyticsMetrics(filteredBookings, { allBookings, activeFrom: rangeWindow.start, activeTo: range === "Custom" ? rangeWindow.end : null, now: nowTick }),
    [filteredBookings, allBookings, rangeWindow, range, nowTick]
  );

  if (role === "caretaker" || role === "warden") {
    return (
      <main className="flex-1 p-8 text-center text-gray-500">
        Redirecting...
      </main>
    );
  }

  // ======================== CALCULATIONS ========================
  // Card values come from the same rows shown in each popup (see utils/guestRoomAnalytics.js).
  const { cards } = metrics;
  const totalBookings = cards.totalBookings;
  const amountReceived = cards.amountReceived;
  const activeGuests = cards.activeGuests;
  const pendingAmount = cards.pendingAmount;
  const cancelledAmount = cards.cancelledAmount;

  // Booking Status: raw booking.status only (Active Guests is a separate metric)
  const {
    booked: bookedCount,
    checkedIn: checkedInCount,
    checkedOut: checkedOutCount,
    cancelled: cancelledCount,
    noShow: noShowCount,
  } = getBookingStatusCounts(filteredBookings);

  // Payment Statistics
  const totalDiscount = filteredBookings.reduce((sum, b) => sum + (Number(b.discount) || 0), 0);

  // Mutually-exclusive buckets that add up to Total Bookings
  const {
    fullyPaid: fullyPaidCount,
    partiallyPaid: partiallyPaidCount,
    unpaid: unpaidCount,
    cancelledNoShow: cancelledNoShowCount,
    free: freeCount,
  } = getPaymentStatusCounts(filteredBookings);

  // Status Distribution (same counts as the text card)
  const statusData = [
    { name: "Booked", value: bookedCount, color: COLORS.info },
    { name: "Checked In", value: checkedInCount, color: COLORS.warning },
    { name: "Checked Out", value: checkedOutCount, color: COLORS.success },
    { name: "Cancelled Bookings", value: cancelledCount, color: COLORS.danger },
    { name: "No Show", value: noShowCount, color: "#6B7280" },
  ].filter(item => item.value > 0);

  // Payment Status Distribution (same counts as the text card)
  const paymentStatusData = [
    { name: "Fully Paid", value: fullyPaidCount, color: COLORS.success },
    { name: "Partially Paid", value: partiallyPaidCount, color: COLORS.warning },
    { name: "Unpaid", value: unpaidCount, color: COLORS.danger },
    { name: "Free Bookings", value: freeCount, color: COLORS.secondary },
    { name: "Cancelled / No Show", value: cancelledNoShowCount, color: "#6B7280" },
  ].filter(item => item.value > 0);

  // Hostel-wise Distribution
  const hostelCounts = {};
  filteredBookings.forEach(b => {
    hostelCounts[b.hostel] = (hostelCounts[b.hostel] || 0) + 1;
  });

  const hostelData = Object.entries(hostelCounts).map(([name, count]) => ({
    name,
    bookings: count,
  }));

  // Amount received by Hostel (same rows as the Amount Received popup)
  const hostelRevenue = {};
  metrics.rows.received.forEach(row => {
    hostelRevenue[row.hostel] = (hostelRevenue[row.hostel] || 0) + row.amount;
  });

  const revenueData = Object.entries(hostelRevenue).map(([name, revenue]) => ({
    name,
    revenue: Math.round(revenue),
  }));

  // Trends: sorted by real period key, formatted only for display
  const trendData = buildTrend(filteredBookings, range, undefined, rangeWindow)
    .map(({ period, value }) => ({ period, count: value }));

  const revenueTrendData = buildTrend(filteredBookings, range, getReceivedAmount, rangeWindow)
    .map(({ period, value }) => ({ period, revenue: Math.round(value) }));

  // CSV Download
  const handleDownloadCSV = () => {
    if (filteredBookings.length === 0) {
      showToast("No booking data available.", "info");
      return;
    }

    const headers = [
      "Name", "Email", "Phone", "Hostel", "Room No", "Check-in", "Check-out",
      "Status", "Reported Status", "Payment Type", "Total Amount", "Paid Amount",
      "Balance", "Discount", "Payment Status"
    ];

    const rows = filteredBookings.map(b => [
      b.guest || "—",
      b.email || "—",
      b.contact || "—",
      b.hostel,
      b.roomNo,
      b.from ? format(b.from, "dd-MMM-yyyy") : "—",
      b.to ? format(b.to, "dd-MMM-yyyy") : "—",
      b.status,
      b.reportedStatus || "pending",
      b.paymentType,
      b.totalAmount || 0,
      b.paidAmount || 0,
      b.balanceAmount || 0,
      b.discount || 0,
      b.paymentStatus || "—"
    ]);

    rows.push(["", "", "", "", "", "", "", "", "", "Amount Received", "", amountReceived, "", totalDiscount]);

    const csvContent =
      headers.join(",") +
      "\n" +
      rows.map(r => r.map(v => `"${String(v || "")}"`).join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `analytics_${range === "Custom" ? "custom" : range.toLowerCase()}_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.main
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="flex-1 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 md:mb-8">
          <div className="w-full md:w-auto">
            <h1 className="text-2xl md:text-4xl font-extrabold text-gray-900 mb-1 md:mb-2">📊 Analytics</h1>
            <p className="text-xs md:text-sm text-gray-600">Booking & revenue analytics</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            <button
              onClick={handleDownloadCSV}
              className="px-3 md:px-4 py-2 text-xs md:text-sm rounded-lg md:rounded-xl bg-green-600 text-white hover:bg-green-700 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all"
            >
              <DownloadIcon className="w-3 h-3 md:w-4 md:h-4" />
              CSV
            </button>
            <button
              onClick={() => setActiveTab("Home")}
              className="px-3 md:px-4 py-2 text-xs md:text-sm rounded-lg md:rounded-xl bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg transition-all"
            >
              🏠 Home
            </button>
          </div>
        </div>

        {/* Range Selector */}
        <div className="flex flex-wrap gap-2 mb-6 md:mb-8">
          {["Monthly", "Quarterly", "Annual", "Overall"].map((r) => (
            <button
              key={r}
              onClick={() => selectPreset(r)}
              className={`px-2 md:px-4 py-1 md:py-2 text-xs md:text-sm rounded-lg md:rounded-xl font-medium transition-all ${
                range === r
                  ? "bg-red-600 text-white shadow-md"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              {r}
            </button>
          ))}
          <button
            onClick={() => setPickerOpen((open) => !open)}
            aria-expanded={pickerOpen}
            className={`px-2 md:px-4 py-1 md:py-2 text-xs md:text-sm rounded-lg md:rounded-xl font-medium transition-all ${
              range === "Custom"
                ? "bg-red-600 text-white shadow-md"
                : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
            }`}
          >
            Date Range
          </button>
          {pickerOpen && (
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <label className="flex items-center gap-1 text-xs md:text-sm text-gray-600">
                From
                <input
                  type="date"
                  aria-label="From date"
                  value={draftFrom}
                  max={draftTo || undefined}
                  onChange={(e) => setDraftFrom(e.target.value)}
                  className="px-2 py-1 md:py-2 text-xs md:text-sm rounded-lg md:rounded-xl border border-gray-200 bg-white text-gray-700"
                />
              </label>
              <label className="flex items-center gap-1 text-xs md:text-sm text-gray-600">
                To
                <input
                  type="date"
                  aria-label="To date"
                  value={draftTo}
                  min={draftFrom || undefined}
                  onChange={(e) => setDraftTo(e.target.value)}
                  className="px-2 py-1 md:py-2 text-xs md:text-sm rounded-lg md:rounded-xl border border-gray-200 bg-white text-gray-700"
                />
              </label>
              <button
                onClick={applyCustomRange}
                className="px-2 md:px-4 py-1 md:py-2 text-xs md:text-sm rounded-lg md:rounded-xl font-medium bg-red-600 text-white shadow-md hover:bg-red-700 transition-all"
              >
                Apply
              </button>
              <button
                onClick={clearCustomRange}
                className="px-2 md:px-4 py-1 md:py-2 text-xs md:text-sm rounded-lg md:rounded-xl font-medium bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 transition-all"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 md:py-20">
            <div className="animate-spin rounded-full h-12 md:h-16 w-12 md:w-16 border-b-4 border-red-600 mb-4"></div>
            <p className="text-sm md:text-base text-gray-600 font-semibold">Loading analytics data...</p>
          </div>
        )}

        {/* No Data State */}
        {!loading && filteredBookings.length === 0 && (
          <div className="bg-white rounded-lg md:rounded-2xl p-6 md:p-12 text-center shadow-lg border border-gray-200">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 md:w-24 md:h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-gray-700 mb-2">No Booking Data</h3>
            <p className="text-sm md:text-base text-gray-500 mb-4">No bookings to display analytics for the selected range.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 md:px-6 py-2 md:py-3 bg-red-600 text-white text-sm md:text-base rounded-lg md:rounded-xl hover:bg-red-700 transition"
            >
              Retry
            </button>
          </div>
        )}

        {/* Show content only when not loading and has data */}
        {!loading && filteredBookings.length > 0 && (
          <>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-6 mb-6 md:mb-8">
          <StatCard
            title="Total Bookings"
            value={totalBookings}
            subtitle={rangeWindow.subtitle}
            icon={UsersIcon}
            color="info"
            onClick={() => setActiveMetric("total")}
          />
          <StatCard
            title="Amount Received"
            value={`₹${amountReceived.toLocaleString()}`}
            subtitle={`${metrics.rows.received.length} bookings with payments`}
            icon={IndianRupeeIcon}
            color="success"
            onClick={() => setActiveMetric("received")}
          />
          <StatCard
            title="Active Guests"
            value={activeGuests}
            subtitle={rangeWindow.activeSubtitle}
            icon={CheckCircleIcon}
            color="warning"
            onClick={() => setActiveMetric("active")}
          />
          <StatCard
            title="Pending Amount"
            value={`₹${pendingAmount.toLocaleString()}`}
            subtitle={`${metrics.rows.pending.length} bookings with balance due`}
            icon={ClockIcon}
            color="danger"
            onClick={() => setActiveMetric("pending")}
          />
          <StatCard
            title="Cancelled Amount"
            tooltip="Unpaid amount from Paid bookings that are currently Cancelled or No Show."
            value={`₹${cancelledAmount.toLocaleString()}`}
            subtitle={`${metrics.rows.cancelled.length} cancelled / no-show bookings`}
            icon={TrendingUpIcon}
            color="secondary"
            onClick={() => setActiveMetric("cancelled")}
          />
        </div>
        <p className="-mt-3 md:-mt-5 mb-6 md:mb-8 text-[11px] text-gray-400">
          Period based on booking check-in date.
        </p>

        {activeMetric && (
          <AnalyticsDetailModal
            metricKey={activeMetric}
            rows={metrics.rows[activeMetric]}
            periodNote={rangeWindow.subtitle}
            onClose={closeMetric}
          />
        )}

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white rounded-lg md:rounded-2xl p-4 md:p-6 shadow-lg border border-gray-100">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Booking Status</h3>
            <div className="space-y-2 md:space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Booked</span>
                <span className="text-sm md:font-bold text-blue-600">{bookedCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Checked In</span>
                <span className="text-sm md:font-bold text-orange-600">{checkedInCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Checked Out</span>
                <span className="text-sm md:font-bold text-green-600">{checkedOutCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <InfoTip className="text-xs md:text-sm text-gray-600" text="All bookings whose current booking status is Cancelled, including Paid and Free bookings.">Cancelled Bookings</InfoTip>
                <span className="text-sm md:font-bold text-red-600">{cancelledCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">No Show</span>
                <span className="text-sm md:font-bold text-gray-600">{noShowCount}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg md:rounded-2xl p-4 md:p-6 shadow-lg border border-gray-100">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Payment Status</h3>
            <div className="space-y-2 md:space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Fully Paid</span>
                <span className="text-sm md:font-bold text-green-600">{fullyPaidCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Partially Paid</span>
                <span className="text-sm md:font-bold text-orange-600">{partiallyPaidCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Unpaid</span>
                <span className="text-sm md:font-bold text-red-600">{unpaidCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <InfoTip className="text-xs md:text-sm text-gray-600" text="Bookings with payment type Free, regardless of booking status.">Free Bookings</InfoTip>
                <span className="text-sm md:font-bold text-blue-600">{freeCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <InfoTip className="text-xs md:text-sm text-gray-600" text="Paid-type bookings that are Cancelled or No Show and are not already counted as Fully Paid.">Cancelled / No Show</InfoTip>
                <span className="text-sm md:font-bold text-gray-600">{cancelledNoShowCount}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg md:rounded-2xl p-4 md:p-6 shadow-lg border border-gray-100">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Financial Summary</h3>
            <div className="space-y-2 md:space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Amount Received</span>
                <span className="text-sm md:font-bold text-green-600">₹{amountReceived.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Wave Off</span>
                <span className="text-sm md:font-bold text-orange-600">₹{totalDiscount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Pending</span>
                <span className="text-sm md:font-bold text-red-600">₹{pendingAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Cancelled</span>
                <span className="text-sm md:font-bold text-gray-600">₹{cancelledAmount.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6 mb-6 md:mb-8">
          {/* Booking Status Pie */}
          <div className="bg-white rounded-lg md:rounded-2xl p-4 md:p-6 shadow-lg border border-gray-100">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Booking Status</h2>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie 
                  data={statusData} 
                  dataKey="value" 
                  cx="50%" 
                  cy="50%" 
                  outerRadius={70}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Payment Status Pie */}
          <div className="bg-white rounded-lg md:rounded-2xl p-4 md:p-6 shadow-lg border border-gray-100">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Payment Status</h2>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie 
                  data={paymentStatusData} 
                  dataKey="value" 
                  cx="50%" 
                  cy="50%" 
                  outerRadius={70}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {paymentStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Hostel-wise Bookings */}
          <div className="bg-white rounded-lg md:rounded-2xl p-4 md:p-6 shadow-lg border border-gray-100">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Bookings by Hostel</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={hostelData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" stroke="#6B7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Bar dataKey="bookings" radius={[8, 8, 0, 0]} fill={COLORS.primary} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue by Hostel */}
          <div className="bg-white rounded-lg md:rounded-2xl p-4 md:p-6 shadow-lg border border-gray-100">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Revenue by Hostel</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" stroke="#6B7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                <Bar dataKey="revenue" radius={[8, 8, 0, 0]} fill={COLORS.success} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Trend Charts */}
        <div className="grid grid-cols-1 gap-3 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white rounded-lg md:rounded-2xl p-4 md:p-6 shadow-lg border border-gray-100">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Booking Trend ({range === "Custom" ? "Date Range" : range})</h2>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="period" stroke="#6B7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  stroke={COLORS.primary} 
                  fillOpacity={1} 
                  fill="url(#colorBookings)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg md:rounded-2xl p-4 md:p-6 shadow-lg border border-gray-100">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Revenue Trend ({range === "Custom" ? "Date Range" : range})</h2>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueTrendData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="period" stroke="#6B7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke={COLORS.success} 
                  fillOpacity={1} 
                  fill="url(#colorRevenue)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-200">
          <p className="text-xs md:text-sm text-gray-500">
            Last updated: {format(new Date(), "PPpp")}
          </p>
          <div className="flex flex-col items-center gap-1">
            <p className="text-[10px] text-slate-500 font-medium">
              Created and Maintained by <span className="text-slate-700">Office of Student Affairs</span>
            </p>
          </div>
        </div>
      </>
    )} 
    </div> 
  </motion.main>
);
}
